import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldPlus, Truck, Undo2 } from 'lucide-react';
import { Alert, Button, CoverageMeter, Icon, ProductThumb, ShipmentMeter, StatusPill } from '@/components/common';
import type { PillTone } from '@/components/common/StatusPill';
import type { Policy } from '@/types';
import type { ProductCase, ProductState } from '@/types/product';
import { addProtectionNotLive } from '@/utils/orderPresentation';
import { coverRemaining, daysUntil, formatDay, formatTimeLeft } from '@/utils/productCases';
import { shipmentPath } from '@/utils/shipment';

const STATUS: Record<ProductState, { tone: PillTone; label: string }> = {
  protected: { tone: 'success', label: 'Protected' },
  not_protected: { tone: 'warning', label: 'Not protected' },
  on_the_way: { tone: 'info', label: 'On the way' },
};

interface ProductCaseCardProps {
  product: ProductCase;
  onFileClaim: (policy: Policy) => void;
  /** True while this card's claim is being opened. */
  filingClaim?: boolean;
}


/** One product on My Plans (Figma "Card/Product case"): protected, not protected or on the way. */
const ProductCaseCard: React.FC<ProductCaseCardProps> = ({ product, onFileClaim, filingClaim }) => {
  const navigate = useNavigate();
  const { tone, label } = STATUS[product.state];
  const dateLine = product.deliveredAt
    ? `Delivered ${formatDay(product.deliveredAt)}`
    : product.orderedAt
      ? `Ordered ${formatDay(product.orderedAt)}`
      : '';
  const subtitle = [product.merchant, dateLine].filter(Boolean).join(' · ');

  return (
    <article className="tw-flex tw-flex-col tw-gap-[14px] tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
      <div className="tw-flex tw-items-center tw-gap-[14px]">
        <ProductThumb src={product.imageUrl} alt={product.title} />
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[3px]">
          <h3 className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">{product.title}</h3>
          {subtitle && <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{subtitle}</p>}
        </div>
        <StatusPill tone={tone}>{label}</StatusPill>
      </div>

      {product.state === 'protected' && product.plan && (
        <>
          <CoverageMeter
            plan={product.plan.name}
            till={`Covered till ${formatDay(product.plan.endDate)}`}
            left={`${formatTimeLeft(product.plan.endDate)} left`}
            remaining={coverRemaining(product.plan.startDate, product.plan.endDate)}
            note={product.plan.note}
          />
          {product.activeClaim && <Alert title="Claim under review">{product.activeClaim.summary}</Alert>}
          <div className="tw-flex tw-gap-2.5">
            {product.activeClaim ? (
              <Button size="sm" className="tw-flex-1" onClick={() => navigate(`/customer/claims/${product.activeClaim!.id}`)}>
                Track claim
              </Button>
            ) : (
              <Button size="sm" className="tw-flex-1" loading={filingClaim} onClick={() => onFileClaim(product.plan!.policy)}>
                File a claim
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              className="tw-flex-1"
              onClick={() => navigate(`/customer/policies/${product.plan!.policy.id}`, { state: { policy: product.plan!.policy } })}
            >
              Plan details
            </Button>
          </div>
        </>
      )}

      {product.state === 'not_protected' && (
        <>
          <Alert tone="warning" title={product.lapsedPlanName ? `${product.lapsedPlanName} has ended` : 'No protection plan yet'}>
            {product.protectionDeadline
              ? `Add Accidental Damage or Extended Warranty until ${formatDay(product.protectionDeadline)} — ${daysUntil(product.protectionDeadline)} days left.`
              : 'Add Accidental Damage or Extended Warranty to protect this product.'}
          </Alert>
          {product.returnWindow?.open && (
            <p className="tw-flex tw-items-center tw-gap-2 tw-text-[12px] tw-font-medium tw-leading-4 tw-text-ds-text-tertiary">
              <Icon icon={Undo2} size={14} />
              {product.returnWindow.endsAt ? `Return window open till ${formatDay(product.returnWindow.endsAt)}` : 'Return window open'}
            </p>
          )}
          <div className="tw-flex tw-gap-2.5">
            <Button size="sm" leadingIcon={ShieldPlus} className="tw-flex-1" onClick={addProtectionNotLive}>
              Add protection
            </Button>
            {product.returnAction && (
              <Button
                size="sm"
                variant="secondary"
                className="tw-flex-1"
                disabled={product.returnAction.disabled}
                onClick={() => product.returnAction?.path && navigate(product.returnAction.path)}
              >
                {product.returnAction.label}
              </Button>
            )}
          </div>
        </>
      )}

      {product.state === 'on_the_way' && product.shipment && (
        <>
          <ShipmentMeter
            title={product.shipment.eta ? `Arriving ${product.shipment.eta}` : 'On its way'}
            stage={product.shipment.stage}
            detail={product.shipment.detail}
          />
          <div className="tw-flex tw-gap-2.5">
            {product.orderId && (
              <Button size="sm" leadingIcon={Truck} className="tw-flex-1" onClick={() => navigate(shipmentPath(product.orderId!))}>
                Track shipment
              </Button>
            )}
            <Button size="sm" variant="secondary" className="tw-flex-1" onClick={addProtectionNotLive}>
              Add protection
            </Button>
          </div>
        </>
      )}
    </article>
  );
};

export default ProductCaseCard;
